import * as React from "react";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@utils/ui";

const tabsListVariants = cva(
  "inline-flex items-center gap-2 rounded-lg bg-gray-50/50 dark:bg-background-muted p-1.5",
  {
    variants: {
      variant: {
        default: "bg-background-white rounded-xl px-3 py-2",
        simple: "bg-transparent p-0 gap-0",
        pill: "bg-transparent p-0 gap-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const tabsTriggerVariants = cva(
  // font-head (Montserrat) to match Button's label typeface — tabs are
  // interactive labels too (design: Typefaces). text-sm matches
  // buttonVariants' default size so pill tabs and adjacent action buttons
  // read at the same scale.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-head font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary-50 text-primary-900 dark:bg-background-muted dark:text-foreground-muted hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white data-[state=active]:bg-primary-900 dark:data-[state=active]:bg-primary data-[state=active]:text-white dark:data-[state=active]:text-white data-[state=active]:shadow-sm",
        secondary:
          "bg-transparent text-gray-600 dark:text-foreground-muted hover:text-gray-900 dark:hover:text-foreground data-[state=active]:bg-primary-50 dark:data-[state=active]:bg-primary data-[state=active]:text-primary dark:data-[state=active]:text-white data-[state=active]:shadow-sm",
        // Rounded pill tabs used in the Report Studio case-detail header:
        // white bordered chips, primary-filled + shadow when active.
        pill: "rounded-full border border-border bg-background-white px-4 py-3 text-foreground hover:bg-primary-50 data-[state=active]:border-transparent data-[state=active]:bg-primary data-[state=active]:font-semibold data-[state=active]:text-white data-[state=active]:shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-9 px-3 py-1.5 text-sm",
        lg: "h-11 px-5 py-2.5 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("w-full", className)}
      {...props}
    />
  );
}

export interface TabsListProps
  extends
    React.ComponentProps<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

function TabsList({ className, variant, ...props }: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

export interface TabsTriggerProps
  extends
    React.ComponentProps<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

function TabsTrigger({ className, variant, size, ...props }: TabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
